import { cn } from '@lib/utils';
import { useMemo, type HTMLAttributes, type ReactNode } from 'react';


export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZE: Record<AvatarSize, { box: string; text: string; dot: string; ring: string }> = {
  sm: { box: 'h-6 w-6', text: 'text-[10px]', dot: 'h-2 w-2', ring: 'ring-2 ring-white' },
  md: { box: 'h-8 w-8', text: 'text-[11px]', dot: 'h-2 w-2', ring: 'ring-2 ring-white' },
  lg: { box: 'h-10 w-10', text: 'text-[13px]', dot: 'h-2.5 w-2.5', ring: 'ring-2 ring-white' },
  xl: { box: 'h-14 w-14', text: 'text-[16px]', dot: 'h-3 w-3', ring: 'ring-2 ring-white' },
  '2xl': { box: 'h-[72px] w-[72px]', text: 'text-[20px]', dot: 'h-3 w-3', ring: 'ring-2 ring-white' },
};

// 10 deterministic gradients — each name hashes to one.
const GRADIENTS = [
  'bg-gradient-to-br from-emerald-400 to-teal-500',
  'bg-gradient-to-br from-blue-400 to-indigo-500',
  'bg-gradient-to-br from-violet-400 to-purple-500',
  'bg-gradient-to-br from-pink-400 to-rose-500',
  'bg-gradient-to-br from-amber-400 to-orange-500',
  'bg-gradient-to-br from-cyan-400 to-blue-500',
  'bg-gradient-to-br from-fuchsia-400 to-pink-500',
  'bg-gradient-to-br from-lime-400 to-emerald-500',
  'bg-gradient-to-br from-sky-400 to-cyan-500',
  'bg-gradient-to-br from-indigo-400 to-violet-500',
];

function hashName(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  name: string;
  src?: string | null;
  size?: AvatarSize;
  online?: boolean;
}

export function Avatar({
  name,
  src,
  size = 'md',
  online,
  className,
  ...rest
}: AvatarProps): JSX.Element {
  const s = SIZE[size];
  const initials = useMemo(() => initialsOf(name), [name]);
  const gradient = useMemo(
    () => GRADIENTS[hashName(name) % GRADIENTS.length] ?? GRADIENTS[0] ?? '',
    [name],
  );

  return (
    <span
      className={cn('relative inline-flex shrink-0', s.box, className)}
      aria-label={name}
      {...rest}
    >
      {src ? (
        <img
          src={src}
          alt={name}
          className={cn('h-full w-full rounded-full object-cover', s.box)}
        />
      ) : (
        <span
          className={cn(
            'flex h-full w-full items-center justify-center rounded-full font-semibold text-white select-none',
            s.text,
            gradient,
          )}
        >
          {initials}
        </span>
      )}
      {online ? (
        <span
          className={cn(
            'absolute bottom-0 right-0 rounded-full bg-emerald-500',
            s.dot,
            s.ring,
          )}
          aria-label="Online"
        />
      ) : null}
    </span>
  );
}

// --- AvatarGroup ------------------------------------------------------------

export interface AvatarGroupProps {
  avatars: Array<{ name: string; src?: string | null }>;
  size?: AvatarSize;
  max?: number;
  className?: string;
  children?: ReactNode;
}

export function AvatarGroup({
  avatars,
  size = 'md',
  max = 4,
  className,
}: AvatarGroupProps): JSX.Element {
  const visible = avatars.slice(0, max);
  const overflow = Math.max(0, avatars.length - max);
  return (
    <div className={cn('inline-flex items-center -space-x-2.5', className)}>
      {visible.map((a, i) => (
        <span
          key={`${a.name}-${i}`}
          className="relative inline-flex rounded-full ring-2 ring-white"
        >
          <Avatar name={a.name} src={a.src} size={size} />
        </span>
      ))}
      {overflow > 0 ? (
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full ring-2 ring-white bg-apple-gray-200 text-apple-gray-700 font-semibold',
            SIZE[size].box,
            SIZE[size].text,
          )}
        >
          +{overflow}
        </span>
      ) : null}
    </div>
  );
}
